'use client';
import Counter from '@/components/Counter';
import { useStateContext } from '@/context/StateContext';
import { calculatePrice, getProductById, products } from '@/data/products';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Col, Modal, Row, Select, Tooltip } from 'antd';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const VMCProduct = () => {
  const product = getProductById('government-mark-certificate');
  const { onAdd, setShowCart } = useStateContext();

  const [selectedProduct, setSelectedProduct] = useState(product);
  const [validityPeriod, setValidityPeriod] = useState(3);
  const [domainCount, setDomainCount] = useState(1);
  const [logoCount, setLogoCount] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalPricePerYear, setTotalPricePerYear] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleOptionChange = (value) => {
    let newProductId;
    switch (value) {
      case 'mrm':
        newProductId = 'modified-registered-mark';
        break;
      case 'cmc':
        newProductId = 'common-mark-certificate';
        break;
      case 'gmc':
        newProductId = 'government-mark-certificate';
        break;
      default:
        newProductId = 'verified-mark-certificate';
    }
    const newProduct = getProductById(newProductId);
    setSelectedProduct(newProduct);
  };

  useEffect(() => {
    if (selectedProduct) {
      const priceDetails = calculatePrice(selectedProduct, {
        domain: domainCount,
        year: validityPeriod,
        logo: logoCount,
      });

      setTotalPrice(priceDetails.total);
      setTotalPricePerYear(priceDetails.annualPrice);
    }
  }, [selectedProduct, validityPeriod, domainCount, logoCount]);

  const handleBuyNow = () => {
    onAdd(selectedProduct, 1, {
      domain: domainCount,
      year: validityPeriod,
      logo: logoCount,
    });
    setShowCart(true);
  };

  const renderPricingOption = (years, product, domainCount, logoCount) => {
    // Get subscription option directly if available
    let priceDetails;

    if (product.subscriptionOptions && product.subscriptionOptions[years]) {
      const option = product.subscriptionOptions[years];
      // Apply logo and domain multipliers to the subscription price
      const multipliedPrice = option.amount * domainCount * logoCount;

      priceDetails = {
        total: multipliedPrice,
        msrp: product.msrp * years * domainCount * logoCount,
        annualPrice: multipliedPrice / years,
      };

      console.log(`Using subscription pricing for ${product.id}, ${years} years:`, {
        basePrice: option.amount,
        multipliedPrice,
        logoCount,
        domainCount,
        priceId: option.priceId,
        productId: option.productId,
        period: option.period,
      });
    } else {
      // Fall back to calculated price if subscription options aren't available
      priceDetails = calculatePrice(product, {
        domain: domainCount,
        year: years,
        logo: logoCount,
      });

      console.log(
        `Falling back to calculated price for ${product.id}, ${years} years: ${priceDetails.total}`
      );
    }

    const savings = Math.round(priceDetails.msrp - priceDetails.total);
    const discountPercent = Math.round((savings / priceDetails.msrp) * 100);

    // Mobile version (simple)
    const mobileContent = (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} key={`mobile-${years}`}>
        <strong style={{ width: '56px', flexShrink: 0 }}>
          {years} Year{years > 1 ? 's' : ''}
        </strong>
        <span style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
          |
          <s style={{ width: '106px', color: '#999' }}>
            MSRP: ${priceDetails.msrp.toLocaleString()}
          </s>
          <span style={{ width: '64px', color: '#1677ff' }}>
            ${Math.floor(priceDetails.total).toLocaleString()}
          </span>
          <span style={{ width: '50px', color: '#666' }}>{discountPercent}% OFF</span>
        </span>
      </div>
    );

    // Desktop version (full)
    const desktopContent = (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} key={`desktop-${years}`}>
        <strong style={{ width: '56px', flexShrink: 0 }}>
          {years} Year{years > 1 ? 's' : ''}
        </strong>
        <span style={{ color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
          |
          <s style={{ width: '106px', color: '#999' }}>
            MSRP: ${priceDetails.msrp.toLocaleString()}
          </s>
          <span style={{ width: '64px', color: '#1677ff' }}>
            ${Math.floor(priceDetails.total).toLocaleString()}
          </span>
          <span style={{ width: '86px', color: '#05c168' }}>Save ${savings.toLocaleString()}</span>
          <span style={{ width: '50px', color: '#666' }}>{discountPercent}% OFF</span>
        </span>
      </div>
    );

    return (
      <Select.Option key={`option-${years}`} value={years.toString()}>
        <div className="desktop-only" key={`desktop-wrapper-${years}`}>
          {desktopContent}
        </div>
        <div className="mobile-only" key={`mobile-wrapper-${years}`}>
          {mobileContent}
        </div>
      </Select.Option>
    );
  };
  return (
    <>
      <Head>
        <title>Buy a DigiCert Government Mark Certificate for Emails | Brandymail</title>
        <meta
          name="description"
          content="Get the DigiCert Government Mark Certificate. Display verified logos on emails, increase trust, and protect against phishing with Brandymail's VMC for government agencies."
        />
        <meta
          property="og:title"
          content="DigiCert Government Mark Certificate (GMC) for BIMI | Brandymail"
        />
        <meta
          property="og:description"
          content="Get the DigiCert Government Mark Certificate for verified email logos. Designed for government agencies implementing BIMI to enhance trust and security in official communications."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://brandymail.com/products/digicert-government-mark-certificate"
        />
        <meta
          property="og:image"
          content="https://brandymail.com/images/og-digicert-government-mark-certificates.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="DigiCert Government Mark Certificate (GMC) - Official Email Verification"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="DigiCert Government Mark Certificate (GMC) for BIMI | Brandymail"
        />
        <meta
          name="twitter:description"
          content="Get the DigiCert Government Mark Certificate for verified email logos. Designed for government agencies implementing BIMI to enhance trust and security in official communications."
        />
        <meta
          name="twitter:image"
          content="https://brandymail.com/images/twitter-digicert-government-mark-certificates.png"
        />
        <meta
          name="twitter:image:alt"
          content="DigiCert Government Mark Certificate (GMC) - Official Email Verification"
        />
      </Head>
      <style>{`
        @media (max-width: 768px) {
          .desktop-only {
            display: none;
          }
        }
        @media (min-width: 769px) {
          .mobile-only {
            display: none;
          }
        }
      `}</style>
      <section className="section hero v9 mg-bottom-48px">
        <div className="container-default w-container">
          <div className="inner-container _700px---tablet center">
            <div className="w-layout-grid grid-2-columns text-left-default vmc-product">
              <Row gutter={[48, 48]}>
                <Col xs={24} lg={12}>
                  <div className="product-page">
                    <div className="mg-bottom-16px">
                      <div className="flex-horizontal product-page-title">
                        <div className="mg-bottom-8px">
                          <div className="text-100 bold color-accent-1 text-uppercase">
                            STARTING AT ${Math.floor(totalPricePerYear / 12)}/MONTH
                          </div>
                        </div>
                        <h2 className="display-1 color-neutral-100 mg-bottom-0">
                          {selectedProduct.title}
                        </h2>
                      </div>
                    </div>
                    <div className="inner-container">
                      <p className="color-neutral-300 mg-bottom-24px paragraph-large">
                        {selectedProduct.subtext}
                      </p>
                      <div className="w-layout-grid grid-1-column bullet-list">
                        {selectedProduct.bullets &&
                          selectedProduct.bullets.map((bullet, index) => (
                            <div key={index} className="feature-wrapper">
                              <div className="line-rounded-icon feature-icon"></div>
                              <div className="text-200 medium color-neutral-300 paragraph-large">
                                {bullet}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={24} lg={12} className="product-column">
                  <div className="inner-container _598px _100---tablet product-module">
                    <div className="position-relative">
                      <div className="inner-container _85 center">
                        <div className="image-wrapper border-radius-18px---top">
                          <Image
                            src={`/images/brandymail-bimi-${selectedProduct.abbrev}.svg`}
                            loading="eager"
                            className="image cover"
                            alt="Brandymail"
                            height={32}
                            width={220}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card add-cart">
                      <div className="add-cart">
                        <form className="add-cart-default">
                          <div className="mg-bottom-24px">
                            <div className="mg-bottom-24px">
                              <Image
                                src={`/images/digicert-${selectedProduct.abbrev}.svg`}
                                loading="eager"
                                className="image cover"
                                alt="Digicert Verified Mark Certificate"
                                height={32}
                                width={220}
                              />
                            </div>
                            <div className="flex-horizontal">
                              <div className="width-100" role="group">
                                <div role="group">
                                  <label htmlFor="options" className="white">
                                    Select your logo type:{' '}
                                    <Tooltip title="Your logo will determine what type of VMC you qualify for: Registered Mark, Common Mark or Government Mark.">
                                      <QuestionCircleOutlined
                                        style={{ fontSize: '20px', color: '#fff' }}
                                      />
                                    </Tooltip>
                                  </label>
                                  <Select
                                    id="options"
                                    className="select w-select"
                                    onChange={handleOptionChange}
                                    value={selectedProduct.abbrev}
                                  >
                                    {products
                                      .filter((p) => p.type === 'certificate')
                                      .map((p) => (
                                        <Select.Option key={p.abbrev} value={p.abbrev}>
                                          {p.optionText}
                                        </Select.Option>
                                      ))}
                                  </Select>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mg-bottom-24px">
                            <div className="flex-horizontal">
                              <div className="width-100" role="group">
                                <div role="group">
                                  <label htmlFor="subscription-period" className="white">
                                    Choose your subscription period:
                                  </label>
                                  <Select
                                    value={validityPeriod.toString()}
                                    id="subscription-period"
                                    className="select w-select"
                                    defaultValue={3}
                                    onChange={(value) => setValidityPeriod(Number(value))}
                                  >
                                    {[1, 2, 3].map((years) =>
                                      renderPricingOption(
                                        years,
                                        selectedProduct,
                                        domainCount,
                                        logoCount
                                      )
                                    )}
                                  </Select>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mg-bottom-16px">
                            <label htmlFor="logo-counter" className="white">
                              How many unique logos do you want to certify?{' '}
                              <Tooltip
                                title={`Each VMC certifies one unique logo. If you have multiple logos (e.g., Logo A and Logo B), each requires a separate VMC.`}
                              >
                                <QuestionCircleOutlined
                                  style={{ fontSize: '20px', color: '#fff' }}
                                />
                              </Tooltip>
                            </label>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                width: '100%',
                              }}
                            >
                              <Counter
                                value={logoCount}
                                onChange={setLogoCount}
                                labelSingular="Logo"
                                fullWidth={true}
                              />
                            </div>
                          </div>

                          <div className="mg-bottom-16px">
                            <label htmlFor="domain-counter" className="white">
                              How many sending domains will use this logo?{' '}
                              <Tooltip
                                title={`This refers to the domain you send emails from (eg example.com). First-level sub-domains like mail.example.com are included at no extra cost. You can support up to 250 unique sending domains per certificate/logo (e.g., example.com, testsite.org, company.io) to simplify management.`}
                              >
                                <QuestionCircleOutlined
                                  style={{ fontSize: '20px', color: '#fff' }}
                                />
                              </Tooltip>
                            </label>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                width: '100%',
                              }}
                            >
                              <Counter
                                value={domainCount}
                                onChange={setDomainCount}
                                labelSingular="Domain"
                                fullWidth={true}
                              />
                            </div>
                          </div>

                          <div
                            className="mg-bottom-24px"
                            style={{ display: 'flex', justifyContent: 'center' }}
                          >
                            <div className="add-cart-price-wrapper">
                              <div className="add-cart-price">$</div>
                              <div className="add-cart-price">
                                {Math.floor(totalPricePerYear).toLocaleString()}
                              </div>
                              <div className="add-cart-year mg-bottom-8px color-neutral-600">
                                /year
                              </div>
                            </div>
                          </div>
                          <div
                            className="buttons-row"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              margin: '0 auto',
                              width: '70%',
                            }}
                          >
                            <Link
                              href="#"
                              className="btn-primary w-button width-100"
                              onClick={handleBuyNow}
                            >
                              {`Add ${selectedProduct.abbreviation.toUpperCase()} To Cart`}
                              <span className="line-rounded-icon link-icon-right"></span>
                            </Link>
                          </div>
                          <div className="mg-top-16px text-600 text-center color-neutral-600">
                            {selectedProduct.shortDescription}
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </section>
      <div className="overflow-hidden border-radius-40px">
        <section className="section pd-100px bg-neutral-100">
          <div className="container-default w-container">
            <div className="inner-container _700px---tablet center">
              <div className="w-layout-grid grid-2-columns text-left-default">
                <div className="inner-container top-align">
                  <div className="text-center---tablet">
                    <div className="mg-bottom-8px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        Features &amp; Benefits
                      </div>
                    </div>
                    <h2 className="display-2 mg-bottom-16px">
                      Many marketing &amp; IT security benefits
                    </h2>
                    <p className="mg-bottom-24px paragraph-medium">
                      {selectedProduct.marketingText}
                    </p>
                    <div className="w-layout-grid grid-1-column bullet-list">
                      {selectedProduct.marketingBullets.map((bullet, index) => (
                        <div key={index} className="feature-wrapper">
                          <div className="line-rounded-icon feature-icon"></div>
                          <div className="text-200 color-neutral-900 paragraph-medium">
                            {bullet}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="inner-container _598px _100---tablet top-align">
                  <div className="benefit-wrapper">
                    <div className="mg-bottom-32px">
                      <div className="inner-container _46px">
                        <div className="image-wrapper">
                          <Image
                            src="/images/icon-1-blue-check.svg"
                            loading="eager"
                            alt="Boosted Brand Trust"
                            className="image cover"
                            width={0}
                            height={0}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="benefit-content">
                      <h3 className="display-3 text-semi-bold mg-bottom-16px">
                        {selectedProduct.marketingCallout1Title}
                      </h3>
                      <p className="mg-bottom-0 paragraph-medium">
                        {selectedProduct.marketingCallout1Text}
                      </p>
                    </div>
                  </div>
                  <div className="divider _48px"></div>
                  <div className="benefit-wrapper">
                    <div className="mg-bottom-32px">
                      <div className="inner-container _46px">
                        <div className="image-wrapper">
                          <Image
                            src="/images/icon-3-contact-links-techkit-template.svg"
                            loading="eager"
                            alt="Enhanced Email Authenticity"
                            className="image cover"
                            width={0}
                            height={0}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="benefit-content">
                      <h3 className="display-3 text-semi-bold mg-bottom-16px">
                        {selectedProduct.marketingCallout2Title}
                      </h3>
                      <p className="mg-bottom-0 paragraph-medium">
                        {selectedProduct.marketingCallout2Text}
                      </p>
                    </div>
                  </div>
                  <div className="divider _48px"></div>
                  <div className="benefit-wrapper">
                    <div className="mg-bottom-32px">
                      <div className="inner-container _46px">
                        <div className="image-wrapper">
                          <Image
                            src="/images/icon-8-values-techkit-template.svg"
                            loading="eager"
                            alt="Increased Visibility and Engagement"
                            className="image cover"
                            width={0}
                            height={0}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="benefit-content">
                      <h3 className="display-3 text-semi-bold mg-bottom-16px">
                        {selectedProduct.marketingCallout3Title}
                      </h3>
                      <p className="mg-bottom-0 paragraph-medium">
                        {selectedProduct.marketingCallout3Text}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Modal
        title="Add to Cart"
        open={isModalVisible}
        onOk={handleBuyNow}
        onCancel={() => setIsModalVisible(false)}
      >
        <p>Do you want to add this product to your cart?</p>
      </Modal>
    </>
  );
};

export default VMCProduct;
